<template>
  <div v-if="section.config.newsCarousel">
    <div class="row q-col-gutter-md q-mb-xm">
      <model-form-select
        field="config.newsCarousel.type"
        value-type="String"
        class="col-4"
        :options="CAROUSEL_TYPES.ALL"
        :option-label="type => $_.startCase(type)"
        map-options
        emit-value
        label="Carousel Type"
      />
      <model-form-toggle
        field="config.newsCarousel.controlsVisible"
        value-type="Boolean"
        class="col-2 q-mr-md"
        label="Controls Visible"
        size="lg"
      />
      <model-form-toggle
        field="config.newsCarousel.paginationVisible"
        value-type="Boolean"
        class="col-2 q-mr-md"
        label="Pagination Visible"
        size="lg"
      />
      <model-form-toggle
        field="config.newsCarousel.autoplay"
        value-type="Boolean"
        class="col-auto"
        label="Autoplay"
        @update:model-value="changeCount++"
      />
      <model-form-input
        v-if="section.config.newsCarousel.autoplay"
        field="config.newsCarousel.delayInSeconds"
        value-type="Number"
        class="col"
        label="Delay (seconds)"
        type="number"
        min="0"
        max="60"
        step="0.5"
        @update:model-value="changeCount++"
      />
      <model-form-checkbox
        field="config.newsCarousel.showHeading"
        value-type="Boolean"
        class="col-6 col-sm-3"
        label="Show Heading"
      />
      <model-form-checkbox
        field="config.newsCarousel.showItemTitle"
        value-type="Boolean"
        class="col-6 col-sm-3"
        label="Show Title"
      />
      <model-form-checkbox
        field="config.newsCarousel.showItemSubtitle"
        value-type="Boolean"
        class="col-6 col-sm-3"
        label="Show Subtitle Banner"
      />
      <model-form-checkbox
        field="config.newsCarousel.showItemDescription"
        value-type="Boolean"
        class="col-6 col-sm-3"
        label="Show Description"
      />
      <model-form-checkbox
        field="config.newsCarousel.showItemButton"
        value-type="Boolean"
        class="col-6 col-sm-3"
        label="Show Button"
      />
      <model-form-checkbox
        field="config.newsCarousel.showItemClass"
        value-type="Boolean"
        class="col-6 col-sm-3"
        label="Show Class"
      />
      <model-form-checkbox
        field="config.newsCarousel.showItemDate"
        value-type="Boolean"
        class="col-6 col-sm-3"
        label="Show Date"
      />

      <model-form-select
        v-if="section.config.newsCarousel.showItemDate"
        field="config.newsCarousel.dateType"
        value-type="String"
        class="col-6 col-sm-3"
        label="Default Date Type"
        :options="dateTypeOptions"
        map-options
        emit-value
      />
      <color-selection
        class="col-12 col-sm-6"
        :color-name="section.config.newsCarousel.backgroundColor"
        :custom-color-hex="section.config.newsCarousel.backgroundColorHex"
        label="Background Color"
        @update:model-value="setBackgroundColor"
      />
      <q-select
        v-model="section.newsItemClassIds"
        class="col-6"
        label="News Item Classes"
        :options="newsItemClasses.data"
        multiple
        use-chips
        option-label="name"
        option-value="id"
        map-options
        emit-value
        bottom-slots
      />
      <model-form-checkbox
        field="config.newsCarousel.newsItemKeepImageRatio"
        value-type="Boolean"
        class="col-4 q-pb-md"
        label="Keep Original Image Aspect Ratio"
        @update:model-value="changeCount++"
      />
    </div>
    <template v-if="section.config.newsCarousel.type === CAROUSEL_TYPES.CAROUSEL">
      <div class="row q-mb-md">
        <div class="text-bold">
          Carousel
        </div>
      </div>
      <div class="row q-col-gutter-md q-mb-sm">
        <template v-if="section.config.newsCarousel.showItemButton">
          <model-form-select
            field="config.newsCarousel.buttonType"
            value-type="String"
            class="col-3"
            :options="buttonTypeOptions"
            map-options
            emit-value
            clearable
            label="Button Type"
          />
          <color-selection
            class="col-12 col-sm-6"
            :color-name="section.config.newsCarousel.buttonColor"
            :custom-color-hex="section.config.newsCarousel.buttonColorHex"
            label="Button Color"
            @update:model-value="setButtonColor"
          />
          <div
            v-if="section.config.newsCarousel.buttonType !== BUTTON_TYPES.FLAT"
            class="col-6 col-sm-3"
          >
            <q-item-label>Border Radius</q-item-label>
            <q-slider
              v-model="section.config.newsCarousel.buttonBorderRadius"
              label
              label-always
              switch-label-side
              type="number"
              :min="0"
              :max="32"
              :step="4"
            />
          </div>
        </template>
        <model-form-select
          field="config.newsCarousel.textPosition"
          value-type="String"
          class="col-3"
          :options="$mh.asOptions(TEXT_POSITION_TYPES.PARTIAL)"
          map-options
          emit-value
          label="Text Position"
          @change="changeCount++"
        />
        <model-form-checkbox
          v-if="section.config.widthMode === 'Full Width'"
          field="config.newsCarousel.hasPartialSlide"
          value-type="Boolean"
          class="col-6 col-sm-3"
          label="Show Partial Pre/Next Slides"
        />
      </div>
    </template>
    <template v-if="section.config.newsCarousel.type === CAROUSEL_TYPES.SLIDER">
      <div class="row q-mt-md q-mb-md">
        <div class="text-bold">
          Slider
        </div>
      </div>
      <div class="row q-col-gutter-md q-mb-sm">
        <div
          class="col-6 col-sm-3"
        >
          <q-item-label>Maximum Slides Per View</q-item-label>
          <q-slider
            v-model="section.config.newsCarousel.slidesPerView"
            label
            label-always
            switch-label-side
            type="number"
            :min="1"
            :max="5"
            :step="1"
            @change="changeCount++"
          />
        </div>
        <div class="col-6 col-sm-3">
          <q-item-label>Slider Padding</q-item-label>
          <q-slider
            v-model="section.config.newsCarousel.sliderGutter"
            label
            label-always
            switch-label-side
            type="number"
            :min="0"
            :max="32"
            :step="4"
            @update:model-value="changeCount++"
          />
        </div>
        <div class="col-6 col-sm-3">
          <q-item-label>Slider Border Radius</q-item-label>
          <q-slider
            v-model="section.config.newsCarousel.borderRadius"
            label
            label-always
            switch-label-side
            type="number"
            :min="0"
            :max="32"
            :step="4"
          />
        </div>
        <dateType
          class="col-6 col-sm-3"
        >
          <q-item-label>Shadow</q-item-label>
          <q-slider
            v-model="section.config.newsCarousel.sliderShadow"
            label
            label-always
            switch-label-side
            type="number"
            :min="0"
            :max="10"
            :step="1"
            @update:model-value="changeCount++"
          />
        </dateType>

        <model-form-checkbox
          field="config.newsCarousel.sliderAllowLoop"
          value-type="Boolean"
          class="col-3"
          label="Loop Slides"
        />
        <model-form-checkbox
          field="config.newsCarousel.sliderPaginateGroupedSlides"
          value-type="Boolean"
          class="col-3"
          label="Paginate Slides in Groups"
          @update:model-value="changeCount++"
        />
        <model-form-checkbox
          field="config.newsCarousel.isTextCentred"
          value-type="Boolean"
          class="col-3"
          label="Text Centred"
          @update:model-value="changeCount++"
        />
        <model-form-checkbox
          field="config.newsCarousel.isTextLight"
          value-type="Boolean"
          class="col-3"
          label="Light Text"
          @update:model-value="changeCount++"
        />
      </div>
    </template>
    <preview
      :key="changeCount"
      :section="section"
      class="q-mb-xl"
    />
  </div>
</template>

<script>
import { CAROUSEL_TYPES, TEXT_POSITION_TYPES } from 'src/models/carousel/Carousel'
import { createNewsCarousel } from 'src/models/carousel/NewsCarousel'
import Section, { BUTTON_TYPES, DISPLAY_DATE_TYPES } from 'src/models/page/Section'
import ColorSelection from 'src/modules/admin/components/ColorSelection'
import Preview from 'src/modules/consumer/components/Preview'

export default {
  name: 'NewsCarouselEditor',
  components: {
    Preview,
    ColorSelection
  },
  props: {
    section: {
      type: Section,
      required: true
    }
  },
  configuration: ['publishedNewsItems', 'newsItemClasses'],
  data () {
    return {
      changeCount: 0
    }
  },
  mounted () {
    if (!this.section.config.newsCarousel) {
      this.section.config.newsCarousel = createNewsCarousel()
    }
  },
  created () {
    _.extend(this, {
      dateTypeOptions: this.$mh.asOptions(DISPLAY_DATE_TYPES),
      buttonTypeOptions: this.$mh.asOptions(BUTTON_TYPES),
      TEXT_POSITION_TYPES,
      DISPLAY_DATE_TYPES,
      CAROUSEL_TYPES,
      BUTTON_TYPES
    })
  },
  methods: {
    setBackgroundColor (colorName, customColorHex) {
      this.section.config.newsCarousel.backgroundColor = colorName
      this.section.config.newsCarousel.backgroundColorHex = customColorHex
    },
    setButtonColor (colorName, customColorHex) {
      this.section.config.newsCarousel.buttonColor = colorName
      this.section.config.newsCarousel.buttonColorHex = customColorHex
    }
  }
}

</script>

<style lang="scss" scoped>
.desktop-preview {
  width: 1024px;
  height: 700px;
  border: solid 5px #666;
  border-radius: 10px;
}

.mobile-preview {
  width: 400px;
  height: 800px;
  border: solid 5px #666;
  border-radius: 10px;
}
</style>
